'use client';

import { useCallback, useRef, useState } from 'react';
import Link from 'next/link';
import {
  ConnectionCheck,
  type CheckInfo,
  type CheckStatus,
  type InstantiableCheck,
  type Checker,
} from 'livekit-client';
import { UDPConnectionCheck, TCPConnectionCheck } from '../../lib/protocolChecks';
import styles from '../../styles/ConnectionTest.module.css';

// CheckStatus enum values (mirrored from livekit-client to avoid type-only export issue)
const Status = { IDLE: 0, RUNNING: 1, SKIPPED: 2, SUCCESS: 3, FAILED: 4 } as const;

type CheckStep =
  | { type: 'method'; method: string }
  | { type: 'custom'; check: InstantiableCheck<Checker> };

const CHECK_STEPS: CheckStep[] = [
  { type: 'method', method: 'checkWebsocket' },
  { type: 'method', method: 'checkWebRTC' },
  { type: 'method', method: 'checkTURN' },
  { type: 'method', method: 'checkReconnect' },
  { type: 'method', method: 'checkPublishAudio' },
  { type: 'method', method: 'checkPublishVideo' },
  { type: 'custom', check: UDPConnectionCheck },
  { type: 'custom', check: TCPConnectionCheck },
  { type: 'method', method: 'checkCloudRegion' },
];

type LogMessage = { level: string; message: string };

function groupLogsByProtocol(logs: LogMessage[]) {
  const udp: LogMessage[] = [];
  const tcp: LogMessage[] = [];
  const other: LogMessage[] = [];

  for (const log of logs) {
    if (log.message.startsWith('udp ')) {
      udp.push(log);
    } else if (log.message.startsWith('tcp ')) {
      tcp.push(log);
    } else {
      other.push(log);
    }
  }

  if (udp.length > 0 || tcp.length > 0) {
    return { grouped: true as const, udp, tcp, other };
  }
  return { grouped: false as const, all: logs };
}

function getStatusLabel(status: CheckStatus): string {
  switch (status) {
    case Status.IDLE:
      return 'idle';
    case Status.RUNNING:
      return 'running';
    case Status.SUCCESS:
      return 'success';
    case Status.FAILED:
      return 'failed';
    case Status.SKIPPED:
      return 'skipped';
    default:
      return 'idle';
  }
}

function getStatusText(status: CheckStatus): string {
  switch (status) {
    case Status.IDLE:
      return 'Pending';
    case Status.RUNNING:
      return 'Running';
    case Status.SUCCESS:
      return 'Passed';
    case Status.FAILED:
      return 'Failed';
    case Status.SKIPPED:
      return 'Skipped';
    default:
      return 'Pending';
  }
}

export default function ConnectionTestPage() {
  const [url, setUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<Map<number, CheckInfo>>(new Map());
  const [testComplete, setTestComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);
  const abortRef = useRef(false);

  const generateTokenAndRun = useCallback(async () => {
    if (!url || !apiKey || !apiSecret) {
      setError('Please provide the WSS URL, API Key, and API Secret.');
      return;
    }

    setError(null);
    setIsGeneratingToken(true);

    try {
      const res = await fetch('/api/connection-test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url, apiKey, apiSecret }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to generate test token');
      }

      const data = await res.json();
      // Now run the tests with the generated token
      setIsRunning(true);
      setTestComplete(false);
      setResults(new Map());
      abortRef.current = false;

      const checker = new ConnectionCheck(url, data.token);

      checker.on('checkUpdate', (id: number, info: CheckInfo) => {
        setResults((prev) => {
          const next = new Map(prev);
          next.set(id, info);
          return next;
        });
      });

      for (const step of CHECK_STEPS) {
        if (abortRef.current) break;
        try {
          if (step.type === 'method') {
            await (checker[step.method as keyof ConnectionCheck] as () => Promise<CheckInfo>)();
          } else {
            await checker.createAndRunCheck(step.check);
          }
        } catch {
          // errors are captured via the checkUpdate event
        }
      }

      setTestComplete(true);
      setIsRunning(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to generate test token');
    } finally {
      setIsGeneratingToken(false);
    }
  }, [url, apiKey, apiSecret]);

  const allResults = Array.from(results.values());
  const successCount = allResults.filter((r) => r.status === Status.SUCCESS).length;
  const failedCount = allResults.filter((r) => r.status === Status.FAILED).length;
  const allPassed = testComplete && failedCount === 0;
  const canStart = !!url && !!apiKey && !!apiSecret && !isRunning && !isGeneratingToken;

  const renderLogEntries = (logs: LogMessage[]) =>
    logs.map((log, j) => (
      <div key={j} className={styles.logEntry} data-level={log.level}>
        {log.message}
      </div>
    ));

  const renderLogs = (logs: LogMessage[]) => {
    const result = groupLogsByProtocol(logs);
    if (!result.grouped) {
      return <div className={styles.logs}>{renderLogEntries(result.all)}</div>;
    }
    return (
      <div className={styles.logs}>
        {result.udp.length > 0 && (
          <>
            <div className={styles.protocolLabel}>UDP</div>
            {renderLogEntries(result.udp)}
          </>
        )}
        {result.tcp.length > 0 && (
          <>
            <div className={styles.protocolLabel}>TCP</div>
            {renderLogEntries(result.tcp)}
          </>
        )}
        {result.other.length > 0 && renderLogEntries(result.other)}
      </div>
    );
  };

  return (
    <>
      <main className={styles.main} data-lk-theme="default">
        <div className={styles.container}>
          <Link href="/" className={styles.backLink}>
            &larr; Back to Call
          </Link>

          <div className="header" style={{ maxWidth: '100%', paddingInline: 0 }}>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '0.25rem' }}>
              Connection Test
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.6)', marginTop: 0, fontSize: '0.95rem' }}>
              Verify that your Seavoice webrtc server is configured correctly and clients can connect
              successfully.
            </p>
          </div>

          <div className={styles.form}>
            <label htmlFor="lk-url" style={{ fontSize: '0.85rem', fontWeight: 500 }}>
              WSS URL
            </label>
            <input
              id="lk-url"
              type="url"
              placeholder="wss://your-server.seavoice.ai"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={isRunning}
            />

            <label htmlFor="lk-api-key" style={{ fontSize: '0.85rem', fontWeight: 500 }}>
              API Key
            </label>
            <input
              id="lk-api-key"
              type="text"
              placeholder="Your API key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              disabled={isRunning}
            />

            <label htmlFor="lk-api-secret" style={{ fontSize: '0.85rem', fontWeight: 500 }}>
              API Secret
            </label>
            <input
              id="lk-api-secret"
              type="password"
              placeholder="Your API secret"
              value={apiSecret}
              onChange={(e) => setApiSecret(e.target.value)}
              disabled={isRunning}
            />

            {error && (
              <p style={{ color: 'rgb(248, 113, 113)', fontSize: '0.85rem', margin: 0 }}>
                {error}
              </p>
            )}

            <button
              className={`lk-button ${styles.startButton}`}
              onClick={generateTokenAndRun}
              disabled={!canStart}
              type="button"
            >
              {isGeneratingToken
                ? 'Generating token...'
                : isRunning
                  ? 'Running Tests...'
                  : 'Start Test'}
            </button>
          </div>

          {allResults.length > 0 && (
            <div className={styles.results}>
              {allResults.map((info, i) => {
                const statusLabel = getStatusLabel(info.status);
                return (
                  <div key={i} className={styles.checkItem} data-status={statusLabel}>
                    <div className={styles.checkHeader}>
                      <span className={styles.checkName}>
                        {info.description || info.name}
                      </span>
                      <span className={styles.statusBadge} data-status={statusLabel}>
                        {info.status === Status.RUNNING && (
                          <span className={styles.spinner} style={{ marginRight: '0.35rem' }} />
                        )}
                        {getStatusText(info.status)}
                      </span>
                    </div>
                    {info.logs.length > 0 && renderLogs(info.logs)}
                  </div>
                );
              })}

              {testComplete && (
                <div className={styles.summary} data-result={allPassed ? 'success' : 'failed'}>
                  {allPassed
                    ? `All checks passed (${successCount}/${allResults.length})`
                    : `${failedCount} check${failedCount !== 1 ? 's' : ''} failed, ${successCount} passed out of ${allResults.length}`}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      <footer data-lk-theme="default">
        Connection diagnostics powered by{' '}
        <a href="https://seavoice.ai" rel="noopener">
          Seavoice
        </a>
        .
      </footer>
    </>
  );
}
